/**
 * Turns `[[12.137]]` in model output into a citation node the renderer can
 * replace.
 *
 * The agent marks each claim with the reference of the passage it came from —
 * `[[12]]` a meeting, `[[12#4]]` a passage, `[[12.137]]` a single turn,
 * `[[12.137-141]]` a run of them. Those markers arrive as ordinary text inside
 * paragraphs, list items and table cells, so splitting the rendered React tree
 * afterwards would mean re-implementing markdown. A remark plugin runs before
 * any of that, at the point where the text is still text.
 *
 * **Double brackets.** A single `[12]` is the shape rewrite chunks already use
 * for person references (`[P206]`), so a model quoting a transcript verbatim
 * would light one up as a citation to a meeting it never read. The service
 * writes double brackets for the same reason and holds a half-written one back
 * mid-stream; this must match it. The grammar lives twice, once per package —
 * `backend/src/services/citation-refs.ts` is the other copy, and they only look
 * wrong together, as citations rendering as literal text.
 *
 * The element is `sup`, not a custom `cite-ref`. That is not cosmetic: the
 * pipeline is sanitised (rehype-harden), which strips unknown elements and
 * keeps their children — a custom element rendered as a bare `12.137` in the
 * middle of a sentence, silently, with no way to click it. `sup` survives, is
 * what a footnote marker semantically IS, and is not something a model emits by
 * accident in markdown.
 *
 * `hChildren` carries the reference as visible content as well as an attribute,
 * because an attribute is also a plausible thing for a sanitiser to drop. The
 * renderer prefers the attribute and falls back to the child text.
 */

interface MdastNode {
  type: string
  value?: string
  children?: MdastNode[]
  data?: Record<string, unknown>
}

/**
 * `[[12]]`, `[[12#4]]`, `[[12.137]]`, `[[12.137-141]]`.
 *
 * Bounded digits so a runaway number is left as text rather than treated as a
 * citation that merely failed to resolve.
 */
const MARKER = /\[\[(\d{1,9}(?:#\d{1,6}|\.\d{1,7}(?:-\d{1,7})?)?)\]\]/g

/**
 * Nodes whose text is not prose and must be left exactly as written.
 * `inlineCode` and `code` are separate mdast types rather than `text`, so they
 * never reach the split — this guards their containers.
 */
const OPAQUE = new Set(['code', 'inlineCode', 'html', 'link', 'linkReference', 'definition'])

function citationNode(ref: string): MdastNode {
  return {
    type: 'citationRef',
    data: {
      hName: 'sup',
      hProperties: { 'data-cite': ref },
      hChildren: [{ type: 'text', value: ref }],
    },
  }
}

/** Split one text node; returns null when it holds no marker. */
export function splitTextNode(node: MdastNode): MdastNode[] | null {
  const value = node.value ?? ''
  MARKER.lastIndex = 0
  if (!MARKER.test(value)) return null
  MARKER.lastIndex = 0

  const out: MdastNode[] = []
  let last = 0
  let match: RegExpExecArray | null
  while ((match = MARKER.exec(value)) !== null) {
    if (match.index > last) out.push({ type: 'text', value: value.slice(last, match.index) })
    out.push(citationNode(match[1]!))
    last = match.index + match[0].length
  }
  if (last < value.length) out.push({ type: 'text', value: value.slice(last) })
  return out
}

export function remarkCitations() {
  return (tree: MdastNode) => {
    const walk = (node: MdastNode): void => {
      if (!node.children || OPAQUE.has(node.type)) return
      const next: MdastNode[] = []
      let changed = false
      for (const child of node.children) {
        if (child.type === 'text') {
          const split = splitTextNode(child)
          if (split) {
            next.push(...split)
            changed = true
            continue
          }
        }
        walk(child)
        next.push(child)
      }
      if (changed) node.children = next
    }
    walk(tree)
  }
}
